import { FeatureCollection, Point, Polygon, LineString } from 'geojson';
import LatLon from 'geodesy/latlon-spherical';
import {
  AlertCamera,
  AlertCameraGroup,
  CameraTimeLapseFrameDetails,
  MapAlertCamera,
  MapAlertCameraGroup,
} from 'features/cameras/cameras.types';
import { ZOOM_LEVELS } from 'features/mapping/mapping.constants';
import { differenceInMinutes, subMinutes } from 'date-fns';
import { DataDrivenPropertyValueSpecification } from 'mapbox-gl';
import { groupCamerasByClusterId } from 'features/cameras/hooks/useAlertCameras';
import { sortCamerasByMostRecentMovement } from 'features/cameras/cameras.utils';
import {
  CameraArrow,
  CameraArrowOffline,
  CameraArrowSelected,
  CameraPTZBase,
  CameraPTZBaseOffline,
  CameraPTZBaseSelected,
  CameraStationaryBase,
  CameraStationaryBaseOffline,
  CameraStationaryBaseSelected,
} from 'features/mapping/icons';
import {
  CameraAzLineGeoJson,
  CameraBaseFeatureProperties,
  CameraGeoJson,
  GenericCameraFeatureProperties,
} from './AlertCamerasLayer.types';
import {
  CAMERA_MOVED_UNDER_5MIN_COLOR,
  CAMERA_MOVED_5_TO_15MIN_COLOR,
  AZ_LINE_DISTANCE_MILES,
} from './AlertCamerasLayer.constants';

const KM_IN_MILE = 1.60934;

export const noop = (): void => {};

const getViewShedDistance = (fov: number): number => {
  // No FOV will be larger than 180 (the cams max out at ~63 deg).
  const scaleFactor = (180 - fov) ** 2;
  const scaleConstant = 0.5;
  return scaleConstant * scaleFactor;
};

export const getBaseCameraIcon = (
  cameraGroup: AlertCameraGroup,
  isSelected: boolean,
): string => {
  const { hasPtz } = cameraGroup;
  const isWholeGroupOffline = cameraGroup.cameras.every(
    (camera) => camera.isOffline,
  );

  if (isSelected) {
    return hasPtz
      ? CameraPTZBaseSelected.name
      : CameraStationaryBaseSelected.name;
  }

  if (isWholeGroupOffline) {
    return hasPtz
      ? CameraPTZBaseOffline.name
      : CameraStationaryBaseOffline.name;
  }

  return hasPtz ? CameraPTZBase.name : CameraStationaryBase.name;
};

export const getCameraArrowIcon = (
  camera: Pick<MapAlertCamera, 'isOffline'>,
  isSelected: boolean,
): string => {
  if (isSelected) {
    return CameraArrowSelected.name;
  }

  if (camera.isOffline) {
    return CameraArrowOffline.name;
  }

  return CameraArrow.name;
};

const getViewShedPolygonCoordinates = (
  alertCamera: MapAlertCamera,
  currentTimelapseFrame?: CameraTimeLapseFrameDetails | null,
): Polygon['coordinates'] => {
  const pos = new LatLon(alertCamera.latlng.lat, alertCamera.latlng.lng);
  // If there is a timelapse frame, use the az and fov from that frame.
  const az = currentTimelapseFrame?.azDeg || alertCamera.azDeg || 0;
  const fov = currentTimelapseFrame?.fovDeg || alertCamera.fovDeg;
  const numCurvePoints = 20; // number of points on the "end" of the viewshed.

  // Create a GeoJSON polygon that consists of the camera's location, two points
  // on either side of it's field-of-view a given distance D away.
  const d = getViewShedDistance(fov);

  const points = [];
  const increments = fov / (numCurvePoints - 1);
  for (let i = 0; i < numCurvePoints; i += 1) {
    const angle = az + fov / 2 - i * increments;
    const p = pos.destinationPoint(d, angle);
    points.push([p.lng, p.lat]);
  }

  // GeoJSON polygon outer rings are right-wound (points go clockwise).
  return [[[pos.lng, pos.lat], ...points, [pos.lng, pos.lat]]];
};

export const getBaseCameraName = (name: string): string => {
  if (!name || !name.trim()) {
    return '';
  }

  const patterns = [
    // Case 1: Names with numeric suffix followed by FAA suffix (e.g., "Laie 3 FAA")
    /^(.+?)\s+\d+\s+FAA\b/i,
    // Case 2: Names with identifier suffixes (e.g., "Red Mountain Fixed South")
    /^(.+?)\s+(Fixed|SCC|Elevated|Ground)\b/i,
    // Case 3: Names with directional suffixes (e.g., "Bear Mtn South")
    /^(.+?)\s+(North|South|East|West|NE|NW|SE|SW|SB|NB|WB|EB|N|S|E|W|Northeast|Northwest|Southeast|Southwest)\b/i,
    // Case 4: Names with numeric suffixes (e.g., "Leadville-Lake County 3")
    /^(.+?)\s+\d+$/,
  ];

  const trimmedName = name.trim();

  for (const pattern of patterns) {
    const match = trimmedName.match(pattern);
    if (match) {
      return match[1].trim();
    }
  }

  return trimmedName;
};

const hasAnyCameraAzimuth = (cameraGroup: MapAlertCameraGroup): boolean =>
  cameraGroup.cameras.some((camera) => camera.azDeg != null);

export const toGeojsonArrowFeatures = (
  cameras: MapAlertCameraGroup['cameras'],
): CameraGeoJson[] =>
  cameras.map((alertCamera) => ({
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [alertCamera.latlng.lng, alertCamera.latlng.lat],
    },
    properties: {
      ...alertCamera,
      icon: getCameraArrowIcon(alertCamera, false),
      iconFocused: getCameraArrowIcon(alertCamera, true),
    },
  }));

export const toGeojsonFeatures = (
  cameraGroup: AlertCameraGroup,
  skipAzCheck = false,
): { baseIcons: CameraGeoJson[]; arrowIcons: CameraGeoJson[] } => {
  const { lat, lng } = cameraGroup.latlng;
  const camerasHaveAzimuth = hasAnyCameraAzimuth(cameraGroup);

  const childCameraNames = cameraGroup.cameras.reduce((names, camera) => {
    const cameraName = getBaseCameraName(camera.name);
    if (!names) return cameraName;
    if (names.includes(cameraName)) return names;
    return `${names}\n${cameraName}`;
  }, '');

  // Sort by most recently moved so childCameraIds[0] is the default on cluster
  // tap. Labels (childCameraNames) keep natural order to avoid reshuffling.
  const childCameraIds = sortCamerasByMostRecentMovement(
    cameraGroup.cameras,
  ).map((camera) => camera.id);

  const baseIcon: CameraGeoJson = {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [lng, lat],
    },
    properties: {
      id: cameraGroup.id,
      azDeg: 0,
      ...(!camerasHaveAzimuth && cameraGroup.cameras[0]),
      ...(camerasHaveAzimuth && {
        childCameraIds,
        childCameraNames,
        childCameraNamesLength: childCameraNames.split('\n').length,
      }),
      icon: getBaseCameraIcon(cameraGroup, false),
      iconFocused: getBaseCameraIcon(cameraGroup, true),
    },
  };

  if (!camerasHaveAzimuth && !skipAzCheck) {
    return { baseIcons: [baseIcon], arrowIcons: [] };
  }

  return {
    baseIcons: [baseIcon],
    arrowIcons: toGeojsonArrowFeatures(cameraGroup.cameras),
  };
};

export const toGeojsonViewshedFeatures = (
  cameraGroup: MapAlertCameraGroup,
  currentTimelapseFrame?: CameraTimeLapseFrameDetails | null,
): CameraGeoJson[] => {
  const camerasHaveAzimuth = hasAnyCameraAzimuth(cameraGroup);
  if (!camerasHaveAzimuth) return [];

  const viewShedPolygons: CameraGeoJson[] = cameraGroup.cameras
    .filter((alertCamera) => !alertCamera.isOffline && alertCamera.hasPtz)
    .map((alertCamera) => ({
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: getViewShedPolygonCoordinates(
          alertCamera,
          currentTimelapseFrame,
        ),
      },
      properties: {
        ...alertCamera,
      },
    }));

  return [...viewShedPolygons];
};

export const getCameraIdFromFeature = (
  cameraProperties: GenericCameraFeatureProperties,
): string => {
  if ('childCameraIds' in cameraProperties) {
    const childIds = JSON.parse(
      // maplibre unfortunately stringifies nested objects/arrays
      cameraProperties.childCameraIds as unknown as string,
    );
    return childIds[0];
  }

  return cameraProperties.id!;
};

export const getCameraFromGroup = <G extends MapAlertCameraGroup>(
  cameraGroup: G,
  id: string,
): G['cameras'][number] | undefined =>
  cameraGroup.cameras.find(
    (camera) => camera.id === id || camera.cameraGroupId === id,
  );

export const generateViewshedGeojson = <G extends MapAlertCameraGroup>(input: {
  alertCameraGroups: G[];
  filterFn: (cameraGroup: G) => boolean;
  activeCameraId?: string;
  hoveredCameraId?: string | null;
  currentTimelapseFrame?: CameraTimeLapseFrameDetails | null;
}): FeatureCollection<Point | Polygon, GenericCameraFeatureProperties> => {
  const {
    alertCameraGroups,
    activeCameraId,
    hoveredCameraId,
    currentTimelapseFrame,
    filterFn,
  } = input;
  // Give priority to the camera the user is hovering over, relative to the one
  // they clicked on.
  const viewshedCameraId = hoveredCameraId || activeCameraId;

  // Exit early if there's nothing to highlight
  if (!viewshedCameraId) {
    return {
      type: 'FeatureCollection',
      features: [],
    };
  }

  // Get the camera group that has the viewshed
  const viewshedCameraGroup: G | undefined = alertCameraGroups
    // filter out any of the wrong camera types
    .filter(filterFn)
    // filter out camera groups that don't contain the camera ID we want
    .find((cameraGroup) => getCameraFromGroup(cameraGroup, viewshedCameraId));

  return {
    type: 'FeatureCollection',
    features: viewshedCameraGroup
      ? // If a hovered camera is active, don't pass on the timelapse frame even if
        // one is set. This is to prevent the viewshed from changing based on the
        // metadata of the timelapse frame for the hovered camera (which is different)
        // from the one playing timelapse.
        toGeojsonViewshedFeatures(
          viewshedCameraGroup,
          hoveredCameraId ? undefined : currentTimelapseFrame,
        )
      : [],
  };
};

const hasCameraFeatureChildCameraNames = (feature: CameraGeoJson): boolean => {
  return (
    'childCameraNames' in feature.properties &&
    typeof feature.properties.childCameraNames === 'string' &&
    feature.properties.childCameraNames !== ''
  );
};

const getFeatureWithChildCamerasKey = (feature: CameraGeoJson): string => {
  const [lng, lat] = feature.geometry.coordinates;
  return `${lat},${lng}`;
};

const mergeChildCameraNames = (
  existingNames: string,
  newNames: string,
): string => {
  return `${existingNames}\n${newNames}`;
};

const updateFeatureChildCameraNamesProperty = (
  feature: CameraGeoJson,
  newCameraNames: string,
): CameraGeoJson => {
  return {
    ...feature,
    properties: {
      ...feature.properties,
      childCameraNames: newCameraNames,
      childCameraNamesLength: newCameraNames.split('\n').length,
    },
  };
};

export const getAllCameraLabelsGeoJson = (
  features: CameraGeoJson[],
): FeatureCollection => {
  const parsedFeatures = features.reduce(
    (parsedFeats, feature) => {
      if (!hasCameraFeatureChildCameraNames(feature)) {
        return parsedFeats;
      }

      const key = getFeatureWithChildCamerasKey(feature);
      const existingFeature = parsedFeats[key];

      if (existingFeature) {
        const mergedNames = mergeChildCameraNames(
          (existingFeature.properties as CameraBaseFeatureProperties)
            .childCameraNames!,
          (feature.properties as CameraBaseFeatureProperties).childCameraNames!,
        );
        // eslint-disable-next-line no-param-reassign
        parsedFeats[key] = updateFeatureChildCameraNamesProperty(
          existingFeature,
          mergedNames,
        );
      } else {
        // eslint-disable-next-line no-param-reassign
        parsedFeats[key] = feature;
      }

      return parsedFeats;
    },
    {} as Record<string, CameraGeoJson>,
  );

  return {
    type: 'FeatureCollection',
    features: Object.values(parsedFeatures),
  };
};

const interpolateZoomLevelToMiles = (zoom: number): number => {
  /**
   * Empirical zoom-to-miles: 8->2.5, 9->1.45, 10->0.9, 11->0.45, 12->0.25,
   * 13->0.15, 14->0.08, 15->0.04, 16->0.024, 17->0.012, 18->0.006
   *
   * Simplified via exponential regression (y = a * b^x) to: 341 * 0.548^zoom
   */
  return 341 * 0.548 ** zoom;
};

const getAzLineStringCoordinates = (
  alertCamera: MapAlertCamera,
  offsetMiles: number,
): LineString['coordinates'] => {
  const lat = alertCamera.cluster?.latlng.lat || alertCamera.latlng.lat;
  const lng = alertCamera.cluster?.latlng.lng || alertCamera.latlng.lng;
  const pos = new LatLon(lat, lng);
  const az = alertCamera.azDeg || 0;
  const offsetDistanceM = offsetMiles * KM_IN_MILE * 1000;
  const distanceM = AZ_LINE_DISTANCE_MILES * KM_IN_MILE * 1000;
  const offsetPoint = pos.destinationPoint(offsetDistanceM, az);
  const endPoint = pos.destinationPoint(distanceM, az);
  const coordinates: LineString['coordinates'] = [
    [offsetPoint.lon, offsetPoint.lat],
    [endPoint.lon, endPoint.lat],
  ];
  return coordinates;
};

const getRecentlyMovedCameras = <T extends MapAlertCamera>(
  alertCameras: T[],
): T[] => {
  return alertCameras.filter(
    (alertCamera) =>
      !alertCamera.isOffline &&
      alertCamera.hasPtz &&
      alertCamera.lastMovementAtTimestamp &&
      alertCamera.lastMovementAtTimestamp > subMinutes(new Date(), 15),
  );
};

const toGeojsonAzLineFeatures = (
  alertCameras: MapAlertCamera[],
  offsetMiles: number,
): CameraAzLineGeoJson[] => {
  const azLineStrings: CameraAzLineGeoJson[] = getRecentlyMovedCameras(
    alertCameras,
  ).map((alertCamera) => {
    const color =
      differenceInMinutes(new Date(), alertCamera.lastMovementAtTimestamp!) > 5
        ? CAMERA_MOVED_5_TO_15MIN_COLOR
        : CAMERA_MOVED_UNDER_5MIN_COLOR;
    return {
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: getAzLineStringCoordinates(alertCamera, offsetMiles),
      },
      properties: {
        ...alertCamera,
        color,
      },
    };
  });

  return [...azLineStrings];
};

export const generateAzLineGeojson = (
  alertCameras: MapAlertCamera[],
  zoomLevel = ZOOM_LEVELS.CITY,
): FeatureCollection<LineString> => {
  const offsetMiles = interpolateZoomLevelToMiles(zoomLevel);
  return {
    type: 'FeatureCollection',
    features: toGeojsonAzLineFeatures(alertCameras, offsetMiles),
  };
};

const getAzLineStringCoordsMouseOver = (
  mouseX: number,
  alertCamera: MapAlertCamera,
  currentTimelapseFrame?: CameraTimeLapseFrameDetails | null,
): LineString['coordinates'] => {
  const lat = alertCamera.cluster?.latlng.lat || alertCamera.latlng.lat;
  const lng = alertCamera.cluster?.latlng.lng || alertCamera.latlng.lng;
  const pos = new LatLon(lat, lng);
  const az = currentTimelapseFrame?.azDeg || alertCamera.azDeg || 0;
  const fov = currentTimelapseFrame?.fovDeg || alertCamera.fovDeg;
  // If mouseX is 0, we're at the leftmost edge of the FOV (az - fov/2)
  // If mouseX is 1, we're at the rightmost edge of the FOV (az + fov/2)
  const mouseAzimuth = az - fov / 2 + mouseX * fov;
  const distance = getViewShedDistance(fov);
  const destPoint = pos.destinationPoint(distance, mouseAzimuth);
  const coordinates: LineString['coordinates'] = [
    [pos.lng, pos.lat],
    [destPoint.lng, destPoint.lat],
  ];
  return coordinates;
};

const toGeojsonAzLineMouseOver = (
  mouseX: number,
  alertCameras: MapAlertCamera[],
  activeCameraId: string,
  currentTimelapseFrame?: CameraTimeLapseFrameDetails | null,
): CameraAzLineGeoJson[] => {
  const azLineStrings: CameraAzLineGeoJson[] = alertCameras
    .filter((cam) => cam.hasPtz && cam.id === activeCameraId)
    .map((alertCamera) => {
      return {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: getAzLineStringCoordsMouseOver(
            mouseX,
            alertCamera,
            currentTimelapseFrame,
          ),
        },
        properties: {
          ...alertCamera,
        },
      };
    });

  return [...azLineStrings];
};

export const generateAzimuthLineMouseOverGeojson = (
  mouseX: number,
  alertCameras: MapAlertCamera[],
  activeCameraId?: string,
  currentTimelapseFrame?: CameraTimeLapseFrameDetails | null,
): FeatureCollection<LineString> => {
  return {
    type: 'FeatureCollection',
    features: activeCameraId
      ? toGeojsonAzLineMouseOver(
          mouseX,
          alertCameras,
          activeCameraId,
          currentTimelapseFrame,
        )
      : [],
  };
};

export const hasMultiDirectionFaaCameras = (
  cameraGroup: AlertCameraGroup,
): boolean => {
  const hasFaa =
    cameraGroup.cameras.length > 1 &&
    cameraGroup.cameras.some((camera) => camera.attribution === 'FAA');

  if (!hasFaa) return false;

  const uniqueAzimuths = new Set(
    cameraGroup.cameras.map((camera) => camera.azDeg),
  );
  return uniqueAzimuths.size > 1;
};

export const scaleIconZoomExpression = (
  zoomExpression: DataDrivenPropertyValueSpecification<number>,
  scaleFactor: number,
): DataDrivenPropertyValueSpecification<number> => {
  if (Array.isArray(zoomExpression)) {
    return zoomExpression.map((element, index) => {
      if (typeof element === 'number' && index % 2 === 0) {
        return element * scaleFactor;
      }
      return element;
    }) as DataDrivenPropertyValueSpecification<number>;
  }
  return zoomExpression;
};

export const recentlyMovedCamerasToGeojson = (
  alertCameras: AlertCamera[],
): {
  baseIcons: FeatureCollection<Point | Polygon, GenericCameraFeatureProperties>;
  arrowIcons: FeatureCollection<
    Point | Polygon,
    GenericCameraFeatureProperties
  >;
} => {
  const recentlyMovedCameras = getRecentlyMovedCameras(alertCameras);
  const cameraGroups = groupCamerasByClusterId(recentlyMovedCameras);

  const baseIcons: CameraGeoJson[] = [];
  const arrowIcons: CameraGeoJson[] = [];
  cameraGroups.forEach((camera) => {
    const features = toGeojsonFeatures(camera, true);
    baseIcons.push(...features.baseIcons);
    arrowIcons.push(...features.arrowIcons);
  });

  return {
    baseIcons: {
      type: 'FeatureCollection',
      features: baseIcons,
    },
    arrowIcons: {
      type: 'FeatureCollection',
      features: arrowIcons,
    },
  };
};

export const generateRecentlyMovedCamerasViewshedGeojson = (input: {
  alertCameras: MapAlertCamera[];
  activeCameraId?: string;
  hoveredCameraId?: string | null;
  currentTimelapseFrame?: CameraTimeLapseFrameDetails | null;
}): FeatureCollection<Point | Polygon, GenericCameraFeatureProperties> => {
  const {
    alertCameras,
    activeCameraId,
    hoveredCameraId,
    currentTimelapseFrame,
  } = input;

  const recentlyMovedCameras = getRecentlyMovedCameras(alertCameras);
  const cameraGroups = groupCamerasByClusterId(recentlyMovedCameras);

  return generateViewshedGeojson({
    alertCameraGroups: cameraGroups,
    filterFn: () => true,
    activeCameraId,
    hoveredCameraId,
    currentTimelapseFrame,
  });
};
